import {
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import { useAuth } from '../context/AuthContext';
import config from '../config/config.json';
import AdsSettings from '../components/AdsSettings';

const cardSx = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
};

function InfoItem({ label, value, helper, valueTone = '#111827' }) {
  return (
    <Box>
      <Typography sx={{ color: '#6B7280', fontSize: '13px', fontWeight: 600, mb: 0.75 }}>
        {label}
      </Typography>
      <Typography sx={{ color: valueTone, fontWeight: 700, fontSize: '16px', wordBreak: 'break-word' }}>
        {value}
      </Typography>
      {helper ? (
        <Typography sx={{ color: '#94A3B8', fontSize: '12px', mt: 0.5 }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function SummaryTile({ label, value, caption, tone = 'primary' }) {
  const toneMap = {
    primary: { bg: '#EFF6FF', color: '#2563EB' },
    success: { bg: '#ECFDF5', color: '#059669' },
    warning: { bg: '#FFF7ED', color: '#C2410C' },
    neutral: { bg: '#F8FAFC', color: '#475569' },
  };

  const palette = toneMap[tone] || toneMap.primary;

  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: '14px',
        backgroundColor: palette.bg,
        border: '1px solid #E5E7EB',
      }}
    >
      <Typography sx={{ color: '#6B7280', fontSize: '13px', fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ color: palette.color, fontSize: '28px', fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {caption ? (
        <Typography sx={{ color: '#94A3B8', fontSize: '12px', mt: 0.75 }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

function ServiceCard({ icon, title, value, description }) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        backgroundColor: '#FFFFFF',
        height: '100%',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#EFF6FF',
            color: '#2563EB',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#111827' }}>{title}</Typography>
          <Typography sx={{ color: '#6B7280', fontSize: '13px' }}>{description}</Typography>
        </Box>
      </Stack>
      <Typography sx={{ fontSize: '26px', fontWeight: 800, color: '#111827' }}>{value}</Typography>
    </Box>
  );
}

function SettingsPage() {
  const { admin } = useAuth();

  const endpointGroups = [
    {
      title: 'Kimlik Doğrulama',
      value: '1 endpoint',
      description: 'Admin giriş akışı',
      icon: <SecurityOutlinedIcon fontSize="small" />,
    },
    {
      title: 'Kullanıcı Yönetimi',
      value: '4 endpoint',
      description: 'Listeleme, detay, engelleme ve silme',
      icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
    },
    {
      title: 'Raporlama',
      value: '3 endpoint',
      description: 'Namaz, oruç ve kaza metrikleri',
      icon: <ApiOutlinedIcon fontSize="small" />,
    },
    {
      title: 'Destek Operasyonu',
      value: '2 endpoint',
      description: 'Talep listeleme ve durum güncelleme',
      icon: <DnsOutlinedIcon fontSize="small" />,
    },
  ];

  const hostLabel = (() => {
    try {
      return new URL(config.api_base).host;
    } catch (error) {
      return config.api_base;
    }
  })();

  return (
    <Box>
      <Box sx={{ ...cardSx, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontSize: '30px', fontWeight: 700, color: '#111827', mb: 1 }}>
              Ayarlar
            </Typography>
            <Typography sx={{ color: '#6B7280', maxWidth: 720 }}>
              Admin hesabı, panel ortamı ve bağlı servis mimarisini tek ekranda gözden geçir.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<AdminPanelSettingsOutlinedIcon />}
              label={admin?.role === 'super-admin' ? 'Süper Admin' : 'Admin'}
              sx={{ backgroundColor: '#EFF6FF', color: '#2563EB', fontWeight: 600, borderRadius: '10px', height: 36 }}
            />
            <Chip
              icon={<CloudDoneOutlinedIcon />}
              label={admin?.isActive ? 'Hesap aktif' : 'Hesap pasif'}
              sx={{ backgroundColor: admin?.isActive ? '#ECFDF5' : '#FEF2F2', color: admin?.isActive ? '#059669' : '#DC2626', fontWeight: 600, borderRadius: '10px', height: 36 }}
            />
            <Chip
              icon={<SettingsSuggestOutlinedIcon />}
              label="Production"
              sx={{ backgroundColor: '#F8FAFC', color: '#475569', fontWeight: 600, borderRadius: '10px', height: 36 }}
            />
          </Stack>
        </Stack>
      </Box>

      <Stack spacing={3}>
        <AdsSettings />
        <Box sx={cardSx}>
          <Stack spacing={2.5}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                Yönetici Hesabı
              </Typography>
              <Typography sx={{ color: '#6B7280' }}>
                Oturum sahibi yöneticinin profil ve yetki özeti.
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              <InfoItem label="E-posta" value={admin?.email || '-'} />
              <InfoItem label="İsim" value={admin?.name || '-'} />
              <InfoItem
                label="Rol"
                value={admin?.role === 'super-admin' ? 'Süper Admin' : 'Admin'}
                helper={admin?.role === 'super-admin' ? 'Tam erişim yetkisi' : 'Standart yönetici erişimi'}
                valueTone={admin?.role === 'super-admin' ? '#DC2626' : '#2563EB'}
              />
              <InfoItem
                label="Durum"
                value={admin?.isActive ? 'Aktif' : 'Pasif'}
                helper={admin?.isActive ? 'Panele giriş yapabilir' : 'Erişim kısıtlı'}
                valueTone={admin?.isActive ? '#059669' : '#DC2626'}
              />
            </Box>
          </Stack>
        </Box>

        <Box sx={cardSx}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                Panel ve Sistem
              </Typography>
              <Typography sx={{ color: '#6B7280' }}>
                Çalışan ortam, servis adresi ve güncel yapı bilgileri.
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <SummaryTile label="Ortam" value="Prod" caption="Canlı panel" tone="success" />
              <SummaryTile label="API host" value={hostLabel} caption="Bağlı servis" tone="primary" />
              <SummaryTile label="Panel sürümü" value="v1.0.0" caption="Admin frontend" tone="neutral" />
              <SummaryTile label="Son kontrol" value={new Date().toLocaleDateString('tr-TR')} caption="Yerel tarih" tone="warning" />
            </Box>

            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              <InfoItem label="API Base URL" value={config.api_base} helper="İsteklerin yönlendirildiği temel adres" />
              <InfoItem label="Servis durumu" value="Bağlı" helper="Panel şu anda yapılandırılmış API ile çalışıyor" valueTone="#059669" />
            </Box>
          </Stack>
        </Box>

        <Box sx={cardSx}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                Bağlı Servisler
              </Typography>
              <Typography sx={{ color: '#6B7280' }}>
                Panelin kullandığı yönetim servislerinin operasyonel özeti.
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {endpointGroups.map((group) => (
                <ServiceCard
                  key={group.title}
                  icon={group.icon}
                  title={group.title}
                  value={group.value}
                  description={group.description}
                />
              ))}
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

export default SettingsPage;
